import { InfiniteTable } from '@/components/_common';
import DeleteIcon from '@/components/_common/DeleteIcon';
import EditIcon from '@/components/_common/EditIcon';
import { useCostCenters } from '@/core/domain/enterprise/CostCenter/CostCenter.hooks';
import {
  CostCenter,
  ListCostCentersQuery,
} from '@/core/domain/enterprise/CostCenter/CostCenter.types';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { COLUMNS_WIDTH, getTableScrollX } from '@/utils/table';
import { Divider, Space } from 'antd';
import { ColumnGroupType, ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import { useIntl } from 'react-intl';

interface Props {
  setSelected: (row: CostCenter) => void;
  toggleModal: () => void;
  showRemove: (row: CostCenter) => void;
  queryParams?: ListCostCentersQuery;
}

export default function CostCenterTable({
  setSelected,
  toggleModal,
  showRemove,
  queryParams,
}: Props) {
  const intl = useIntl().formatMessage;

  const translate = (message: string) =>
    intl({ id: `costCenters.domain.${message}` });

  const { data, isFetching, fetchNextPage, hasNextPage } =
    useCostCenters(queryParams);
  const costs = flatInfiniteData(data);

  const columns: ColumnsType<CostCenter> | ColumnGroupType<CostCenter>[] = [
    {
      title: translate('description'),
      key: 'description',
      dataIndex: 'description',
      ellipsis: true,
      width: COLUMNS_WIDTH.default,
    },
    {
      title: translate('updatedAt'),
      key: 'modifiedDate',
      dataIndex: 'modifiedDate',
      width: COLUMNS_WIDTH.date,
      render: (text: string) => (
        <span>{moment(text).format('DD/MM/YYYY')}</span>
      ),
    },
    {
      key: 'action',
      align: 'right',
      width: 100,
      fixed: 'right',
      render: (row: CostCenter) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () => {
                setSelected(row);
                toggleModal();
              },
            }}
            disabled={!hasPermissions([Authorities.REGISTER_COST_CENTER_EDIT])}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => showRemove(row)}
            disabled={
              !hasPermissions([Authorities.REGISTER_COST_CENTER_DELETE])
            }
          />
        </Space>
      ),
    },
  ];
  return (
    <InfiniteTable
      columns={columns}
      hasNextPage={hasNextPage}
      dataSource={costs}
      fetchNextPage={fetchNextPage}
      loading={isFetching}
      rowKey={(row) => row.id}
      scroll={{ x: getTableScrollX(columns) }}
    />
  );
}
